import { DOCUMENT } from '@angular/common';
import { Component, ChangeDetectionStrategy, EffectRef, ElementRef, Injector, OnDestroy, OnInit, ViewEncapsulation, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { v4 } from 'uuid';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { CurrentUserService } from '@jms/ui/ui.standalone.services/current.user.service';
import { agentThemeBridgeStyles, agentThemeStyleId } from '../../styles/agent.theme';
import { AgentChatService } from '../../services/agent.chat.service';
import { AgentContextService } from '../../services/agent.context.service';
import { AgentRevealService } from '../../services/agent.reveal.service';
import { AgentUploadService } from '../../services/agent.upload.service';
import { AgentPanelService, NEW_CHAT_DRAFT_KEY, RESERVED_TAB_ID } from '../../services/agent.panel.service';
import { AgentMessageListComponent } from '../message.list/message.list.component';
import { AgentPromptInputComponent, PROMPT_INPUT_HINTS } from '../prompt.input/prompt.input.component';
import { AgentMentionDropdownComponent } from '../mention.dropdown/mention.dropdown.component';
import { AgentQuestionCardComponent } from '../question.card/question.card.component';
import { AgentConfirmationCardComponent, type ConfirmationCardField, type ConfirmationCardContextItem } from '../confirmation.card/confirmation.card.component';
import { buildToolTitle, buildConfirmationCardFields, buildConfirmationCardContext, extractToolReasoning } from '../confirmation.card/confirmation.card.view.model';
import { AgentShortcutHintsComponent } from '../shortcut.hints/shortcut.hints.component';
import { AgentSuggestActionsComponent } from '../suggest.actions/suggest.actions.component';
import { AgentUtility } from '../../agent.utility';
import { FILE_CONSTRAINTS } from '../../agent.types';
import { mapRoleToSuggestRole } from '../../services/agent.role.mapper';
import { SUGGEST_ACTIONS } from '../../data/agent.suggest.actions.config';

import { AgentThreadFeedbackComponent } from '../thread.feedback/thread.feedback.component';
import { AgentContextGuardNoticeComponent } from '../context.guard.notice/context.guard.notice.component';
import { contextGuardDismissalKey, loadDismissedContextGuards, markContextGuardDismissed } from '../context.guard.notice/context.guard.dismissal';
import type { ContextGuardDismissibleBand, ContextGuardNoticeBand } from '../context.guard.notice/context.guard.types';
import { welcomeTitleForDate } from './welcome.title';
import { AiUsageLimitNoticeComponent, AiUsageLimitService, loadDismissedUsageLimitNotices, markUsageLimitNoticeDismissed, usageLimitDismissalKey } from '../../../core';
import { GuardNoticeComponent } from '@workbuddy/ui/components/guard.notice';
import { AI_PROVIDER_UNAVAILABLE_DETAIL, AI_PROVIDER_UNAVAILABLE_TITLE } from '@jms/model';
import type { AIUsageLimitStatus } from '@jms/model';
import { UIMessage } from 'ai';
import type { AIInterviewAnswer } from '@jms/model';
import type { CardAction, MentionItem, PendingFile, InterviewCardActionMeta, InterviewCardData, ShortcutHint } from '../../agent.types';

// ─── Feedback-prompt heuristics ────────────────────────────────────────────────
// Show the rate-this-conversation prompt once the thread has this many assistant replies…
const FEEDBACK_MIN_REPLIES = 6;
// …or this long after the last reply with no further user activity (the chat has gone quiet),
// but only once the thread has at least this many replies (don't nudge on a short exchange).
const FEEDBACK_IDLE_MS = 45_000;
const FEEDBACK_IDLE_MIN_REPLIES = 4;
// …or when the user's latest message reads like a sign-off / thanks.
const CONVERSATION_CLOSING_RE = /\b(thanks|thank you|thankyou|thx|ta|cheers|perfect|great|awesome|brilliant|that'?s (all|it|great|perfect)|all good|no(thing)? (else|more)|appreciate it|much appreciated|good job|nice one|legend)\b/i;
// Global frequency cap: the prompt may APPEAR at most this many times per rolling window,
// across all chats — so it doesn't nag on every conversation.
const FEEDBACK_MAX_SHOWS = 2;
const FEEDBACK_SHOW_WINDOW_MS = 2 * 60 * 60 * 1000; // 2 hours
// localStorage key holding thread ids already rated/dismissed, so the prompt never re-nags.
const FEEDBACK_HANDLED_KEY = 'agent-feedback-handled';
// localStorage key holding epoch-ms timestamps of recent appearances (for the frequency cap).
const FEEDBACK_SHOWN_AT_KEY = 'agent-feedback-shown-at';

/** Keeps the disabled composer aligned with an honest helper row instead of advertising shortcuts
 *  that no longer work once the conversation is read-only. */
const CONTEXT_GUARD_BLOCKED_HINTS: ShortcutHint[] = [
  { text: 'This chat is read-only' },
  { text: 'Start a new chat to continue' },
];

/** Plain text of a UI message (its text parts joined) — used for the sign-off heuristic. */
function messageText(message: UIMessage): string {

  return (message.parts ?? []).map(part => part as { type?: string; text?: string }).filter(part => part.type === 'text').map(part => part.text ?? '').join(' ');
}

function loadHandledThreads(): Set<string> {

  try {
    const raw = localStorage.getItem(FEEDBACK_HANDLED_KEY);
    return new Set(raw ? JSON.parse(raw) as string[] : []);
  } catch {
    return new Set();
  }
}

function isFeedbackHandled(threadId: string): boolean {

  return loadHandledThreads().has(threadId);
}

function markFeedbackHandled(threadId: string): void {

  try {
    const handled = loadHandledThreads();
    handled.add(threadId);
    localStorage.setItem(FEEDBACK_HANDLED_KEY, JSON.stringify([...handled]));
  } catch {
    // Non-fatal: worst case the prompt can reappear after a reload.
  }
}

/** Appearance timestamps still inside the frequency window (older ones pruned). */
function loadRecentFeedbackShows(nowMs: number): number[] {

  try {
    const raw = localStorage.getItem(FEEDBACK_SHOWN_AT_KEY);
    const timestamps = raw ? JSON.parse(raw) as number[] : [];
    return timestamps.filter(t => typeof t === 'number' && nowMs - t < FEEDBACK_SHOW_WINDOW_MS);
  } catch {
    return [];
  }
}

/** Record an appearance now, persisting only the still-in-window timestamps. */
function recordFeedbackShow(nowMs: number, recent: number[]): void {

  try {
    localStorage.setItem(FEEDBACK_SHOWN_AT_KEY, JSON.stringify([...recent, nowMs]));
  } catch {
    // Non-fatal: worst case the cap resets after a failed write.
  }
}

/**
 * Central orchestrating component for the Agent V2 chat UI.
 * Wires together message list, input bar, welcome UI, and mention dropdown.
 * Manages AgentChatService and AgentUploadService lifecycle at component level.
 *
 * Used by both popup.chat and fullpage.chat layouts -- the $mode input controls sizing/behavior.
 */
@Component({
  selector: 'agent-chat-container',
  standalone: true,
  imports: [AgentMessageListComponent, AgentPromptInputComponent, AgentMentionDropdownComponent, AgentQuestionCardComponent, AgentConfirmationCardComponent, AgentShortcutHintsComponent, AgentSuggestActionsComponent, AgentThreadFeedbackComponent, AgentContextGuardNoticeComponent, AiUsageLimitNoticeComponent, GuardNoticeComponent],
  providers: [AgentChatService, AgentRevealService, AgentUploadService],
  templateUrl: './chat.container.component.html',
  styleUrls: ['./chat.container.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.mode-popup]': '$mode() === "popup"',
    '[class.mode-fullscreen]': '$mode() === "fullscreen"',
    '[class.welcome-active]': '$showWelcome()',
  },
})
export class AgentChatContainerComponent implements OnInit, OnDestroy {

  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly chatService = inject(AgentChatService);
  private readonly revealService = inject(AgentRevealService);
  private readonly uploadService = inject(AgentUploadService);
  private readonly panelService = inject(AgentPanelService);
  private readonly injector = inject(Injector);
  private readonly contextService = inject(AgentContextService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly usageLimits = inject(AiUsageLimitService);

  // This composer's conversation key: the active thread, or the new-chat sentinel while composing an
  // unsaved chat. Drafts are keyed by this so switching to another thread starts with a fresh composer.
  protected readonly $draftKey = computed(() => this.chatService.$threadId() ?? NEW_CHAT_DRAFT_KEY);
  // The draft for the CURRENT conversation only — read by the prompt-input, written via onDraftChange.
  protected readonly $composerDraft = computed(() => this.panelService.composerDraftFor(this.$draftKey()));

  // ─── Inputs ─────────────────────────────────────────────────────────────────

  public readonly $mode = input<'fullscreen' | 'popup'>('fullscreen', { alias: 'mode' });
  public readonly $threadId = input<string | null>(null, { alias: 'threadId' });
  public readonly $interactionMode = input<'interactive' | 'automation'>('interactive', { alias: 'interactionMode' });

  // ─── Outputs ────────────────────────────────────────────────────────────────

  public readonly panelClose = output<void>();
  public readonly threadChanged = output<string>();
  public readonly titleUpdated = output<{ threadId: string; title: string }>();
  // The user asked for a fresh chat from the conversation-limit card. Fullscreen's thread state is
  // owned by the host page (it drives the [threadId] input and the ?threadId query param), so the
  // container asks rather than switching threads behind its back. Popup mode has no host binding —
  // it syncs through the panel service instead — so this is only emitted in fullscreen.
  public readonly newChatRequested = output<void>();

  // ─── Child references ───────────────────────────────────────────────────────

  private readonly messageListRef = viewChild(AgentMessageListComponent);
  private readonly promptInputRef = viewChild(AgentPromptInputComponent);
  private readonly composerOverlayRef = viewChild<ElementRef<HTMLElement>>('composerOverlay');

  /** The one watcher waiting for the composer to appear so it can be focused; see focusInputAfterPanelReady. */
  private pendingFocusRef: EffectRef | null = null;

  // Publishes the floating composer's height as --composer-height on the host so the
  // fullscreen full-bleed scroller can keep pinned-bottom messages clear of the overlay.
  // The overlay only has a layout box in fullscreen chat (display: contents elsewhere),
  // so the observer simply stays quiet in popup/welcome modes.
  private composerResizeObserver: ResizeObserver | null = null;
  private composerHeightRaf = 0;
  private readonly composerHeightTracker = effect(() => {

    const overlay = this.composerOverlayRef()?.nativeElement;
    // Welcome <-> chat flips toggle the overlay between display:contents (no box) and
    // the floating block, AND the prompt-input <-> question-card <-> approval-card swaps
    // change the overlay's height (either card is far taller than the input bar). Re-arm
    // the observer on ALL transitions, or --composer-height can stay stuck at the old/default
    // value and leave the floating card sitting over the stream instead of clearing it.
    this.$showWelcome();
    this.$activeQuestionCard();
    this.$activePendingApproval();
    // The conversation-limit notice changes the overlay's height too — the endcap adds a row above
    // the input, while the blocked quiet rail fuses to the disabled composer. Without this the
    // fullscreen scroller keeps padding for the old height and the notice sits over the transcript.
    this.$contextGuardBand();
    // The spend notice adds/removes a row above the composer (or replaces it) the same way,
    // and so does the provider-unavailable notice. Track only the layout-relevant primitives —
    // never the status object itself — so a refresh that changes nothing visible cannot re-arm
    // the observer (each re-arm forces a measure + style write, which reads as jitter).
    this.$usageLimitBlocked();
    void !!this.$usageLimitNudgeStatus();
    this.$providerUnavailable();
    this.$threadUnavailable();
    this.composerResizeObserver?.disconnect();
    this.composerResizeObserver = null;
    if (this.composerHeightRaf) {
      cancelAnimationFrame(this.composerHeightRaf);
      this.composerHeightRaf = 0;
    }
    if (!overlay) return;

    this.composerResizeObserver = new ResizeObserver(() => {
      // Coalesce a resize burst (the textarea auto-growing as the user types, or a card
      // swapping in mid-stream) into one rAF measurement, so the write that reflows the
      // scroller can't self-feed the observer and drop the final delivery — which would
      // leave --composer-height stale and the card floating over the stream.
      if (this.composerHeightRaf) return;
      this.composerHeightRaf = requestAnimationFrame(() => {
        this.composerHeightRaf = 0;
        const host = this.elementRef.nativeElement;
        // Border-box height (incl. the overlay's own padding) — contentRect would
        // under-report and let pinned-bottom content slide behind the overlay.
        const height = overlay.getBoundingClientRect().height;
        host.style.setProperty('--composer-height', `${Math.ceil(height)}px`);

        // The scroller loses width to its scrollbar while the overlay spans the host,
        // so publish the scrollbar width to keep the strip centred on the message
        // column and the scrollbar outside the blur.
        const scroller = host.querySelector('.messages-area') as HTMLElement | null;
        const scrollbarWidth = scroller ? scroller.offsetWidth - scroller.clientWidth : 0;
        host.style.setProperty('--scroller-scrollbar-w', `${Math.max(0, scrollbarWidth)}px`);
      });
    });
    this.composerResizeObserver.observe(overlay);
  });

  // ─── Local UI state ─────────────────────────────────────────────────────────

  protected readonly $isDragOver = signal(false);
  protected readonly $pendingFiles = signal<PendingFile[]>([]);
  protected readonly $mentionQuery = signal('');
  protected readonly $mentionResults = signal<MentionItem[]>([]);
  protected readonly $mentionVisible = signal(false);
  protected readonly $mentionPosition = signal<{ top: number; left: number }>({ top: 0, left: 0 });

  private dragCounter = 0;
  // The element the drag listeners are bound to (document in fullscreen so the
  // overlay can cover the whole app, the host in popup mode). Kept for teardown.
  private dragListenerTarget: EventTarget | null = null;
  private readonly boundDragEnter = (event: DragEvent): void => this.onDragEnter(event);
  private readonly boundDragLeave = (event: DragEvent): void => this.onDragLeave(event);
  private readonly boundDragOver = (event: DragEvent): void => this.onDragOver(event);
  private readonly boundDrop = (event: DragEvent): void => this.onDrop(event);
  private wasStreaming = false;
  // Latched from Send until the genuine end of the agent turn. This starts before the adapter's
  // streaming signal (tier resolution and request setup happen first), so context notices cannot
  // flash back during that gap. Approval/interview pauses deliberately keep the latch set.
  private readonly $contextGuardTurnActive = signal(false);
  private lastSeenUserMessageId: string | null = null;
  private hasRenderedInitialMessages = false;
  // ─── Feedback prompt (rate-this-conversation, shown above the composer) ───────
  // A slim prompt that invites a thumbs rating for the whole thread. Its triggers (volume,
  // sign-off, gone-quiet) decide eligibility; once eligible AND under the global frequency
  // cap it "activates" for the thread and stays shown until rated/dismissed. Suppressed per
  // thread once handled (persisted), and capped to a few appearances per rolling window overall.
  protected readonly $feedbackHandled = signal(false);
  // Latched true once the prompt has appeared for the current thread (survives streaming/hide).
  private readonly $feedbackActive = signal(false);
  private readonly $feedbackIdleReached = signal(false);
  private feedbackIdleTimer: ReturnType<typeof setTimeout> | null = null;

  /** Trigger conditions for the current thread (independent of the frequency cap). */
  private readonly $feedbackTriggered = computed(() => {

    if (this.$feedbackHandled() || this.chatService.$isStreaming() || !this.chatService.$threadId()) return false;
    const messages = this.chatService.$messages();
    const assistantReplies = messages.filter(m => m.role === 'assistant').length;
    if (assistantReplies === 0) return false;
    const last = messages[messages.length - 1];
    // Enough back-and-forth (volume), the user signed off, or the chat has gone quiet since the
    // last reply — the idle nudge only after a minimum number of replies.
    const soundsFinished = last?.role === 'user' && CONVERSATION_CLOSING_RE.test(messageText(last));
    const volume = assistantReplies >= FEEDBACK_MIN_REPLIES;
    const idle = this.$feedbackIdleReached() && assistantReplies >= FEEDBACK_IDLE_MIN_REPLIES;
    return volume || soundsFinished || idle;
  });

  /** Whether to show the prompt right now: activated for this thread, not handled, not streaming. */
  protected readonly $showFeedbackPrompt = computed(() => this.$feedbackActive() && !this.$feedbackHandled() && !this.chatService.$isStreaming() && !!this.chatService.$threadId());
  // Set in onSend, consumed by the messages effect — distinguishes "user
  // just submitted" (anchor at top) from "thread loaded with history"
  // (jump to bottom) when the messages array transitions from empty to
  // populated. Both cases share newUserMessage + initialRender = true.
  private pendingAnchorOnSend = false;

  // ─── Computed state ─────────────────────────────────────────────────────────

  /** Show welcome screen when there are no messages and no active thread */
  protected readonly $showWelcome = computed(() => {

    const messages = this.chatService.$messages();
    return messages.length === 0 && !this.chatService.$threadId();
  });

  // Picked once per mount so the heading never swaps under the user if they sit on it past midnight.
  protected readonly welcomeTitle = welcomeTitleForDate(new Date());

  /** Lock the model-tier selector once a chat has started (any messages or an active thread).
   *  A brand-new empty chat (no messages, no thread) stays unlocked. */
  protected readonly $modelLocked = computed(() => this.chatService.$messages().length > 0 || !!this.chatService.$threadId());

  /** The tier the composer should show for the active chat, passed to the composer so it shows + locks
   *  to whatever this chat ran on. A chat with a stored tier shows it; an existing chat with no stored
   *  tier (a pre-feature thread) falls back to 'default' and locks; a brand-new chat returns null so the
   *  composer uses the user's persisted preference instead. */
  protected readonly $threadModelTier = computed<import('../../agent.types').AgentModelTier | null>(() => {

    const stored = this.chatService.$activeThreadModelTier();
    if (stored) return stored;
    return this.$modelLocked() ? 'default' : null;
  });

  /** Whether any file is still uploading */
  protected readonly $hasUploadingFiles = computed(() => this.$pendingFiles().some(f => f.status === 'uploading'));

  /** Active (unanswered) question card — overlays the prompt input when present.
   *  The interview tool pauses via needsApproval, so the card reads the pending
   *  tool call's INPUT (the model-proposed questions) while the stream is
   *  suspended in `approval-requested` state. Once the user submits and the
   *  stream resumes, the part transitions to `output-available` with the
   *  structured answers and this computed returns null so the input bar reappears. */
  protected readonly $activeQuestionCard = computed<{ callId: string; data: InterviewCardData } | null>(() => {

    const messages = this.chatService.$messages();
    const cardActions = this.chatService.$cardActions();

    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== 'assistant') continue;
      for (const part of msg.parts) {
        if (!AgentUtility.isToolPartWithCallId(part)) continue;
        const toolName = AgentUtility.partField(part, 'toolName', '') || (part.type as string).replace(/^tool-/, '');
        if (toolName !== 'interview') continue;
        const state = AgentUtility.partField<string>(part, 'state', '');
        if (state !== 'approval-requested') continue;
        const callId = AgentUtility.partField(part, 'toolCallId', '');
        if (cardActions[callId]) continue;
        const input = AgentUtility.partField<unknown>(part, 'input', undefined);
        if (!input || typeof input !== 'object') continue;
        const questions = (input as { questions?: unknown }).questions;
        if (!Array.isArray(questions)) continue;
        return { callId, data: { questions, presented: true } as InterviewCardData };
      }
    }
    return null;
  });

  // Call IDs the user has approved/declined this session but whose tool part hasn't flipped out of
  // 'approval-requested' yet (the resume stream lands a beat later). Excluding these from
  // $activePendingApproval lets the input bar return the instant the user clicks, instead of waiting
  // for the stream — mirrors how $cardActions gates the interview card, but $cardActions is only
  // written for interviews, so write-tool approvals need their own optimistic set. Per-thread and
  // tiny (one entry per resolved approval), so it's left to accumulate harmlessly rather than swept.
  private readonly resolvedApprovalCallIds = signal<Set<string>>(new Set());

  /** The single pending write-tool approval awaiting a decision, as a ready-to-render card view-model,
   *  or null when none is pending. Only ONE card is ever pending at a time (interview OR approval, never
   *  both, never two approvals), so this scans newest-first for the first write-tool part still in
   *  `approval-requested` — excluding the interview tool (it has its own overlay) and any call already
   *  resolved (via $cardActions or the optimistic resolvedApprovalCallIds set). The composer overlay
   *  renders this in place of the input bar, exactly like the interview question card.
   *
   *  `callId` doubles as the approval id: the adapter sets `approval.id = toolCallId`, and
   *  approve/declineToolCall take that same id — so one field drives both the resolved-set exclusion
   *  ($cardActions is keyed by toolCallId) and the approve/decline calls. */
  protected readonly $activePendingApproval = computed<{ callId: string; displayName: string; fields: ConfirmationCardField[]; context: ConfirmationCardContextItem[]; reasoning: string } | null>(() => {

    const messages = this.chatService.$messages();
    const cardActions = this.chatService.$cardActions();
    const resolved = this.resolvedApprovalCallIds();
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== 'assistant') continue;
      for (const part of msg.parts) {
        if (!AgentUtility.isToolPartWithCallId(part)) continue;
        const toolName = AgentUtility.partField(part, 'toolName', '') || (part.type as string).replace(/^tool-/, '');
        if (toolName === 'interview') continue;
        if (AgentUtility.partField<string>(part, 'state', '') !== 'approval-requested') continue;
        const callId = AgentUtility.partField(part, 'toolCallId', '');
        if (cardActions[callId] || resolved.has(callId)) continue;
        const input = AgentUtility.partField<unknown>(part, 'input', {});
        return { callId, displayName: buildToolTitle(toolName, input), fields: buildConfirmationCardFields(toolName, input), context: buildConfirmationCardContext(toolName, input), reasoning: extractToolReasoning(toolName, input) };
      }
    }
    return null;
  });

  /** Whether a write-tool approval card is awaiting a decision. Derived from $activePendingApproval so
   *  the two never disagree. It pauses the turn the same way the interview card does (the adapter flips
   *  to 'idle'), so the streaming-completion side-effects must skip while it waits — otherwise
   *  clearFollow() collapses the anchor spacer and a short conversation strands the card at the top. */
  protected readonly $hasPendingApproval = computed<boolean>(() => !!this.$activePendingApproval());

  // ─── Conversation-length guard (context guard) ───────────────────────────────
  // The backend measures how much of the model's context this chat is consuming and reports a band
  // on the assistant's messages (and live, mid-turn, during a long run). The UI turns that into a
  // gentle nudge and — at the hard limit — a compact rail fused to the disabled composer.

  // Dismissed "thread:band" keys. Seeded from durable browser storage so an explicit dismissal does
  // not nag the user again when they return to the same long conversation.
  private readonly $dismissedGuards = signal<Set<string>>(loadDismissedContextGuards());

  /** The guard band to surface right now, or null when nothing should show.
   *
   *  Three rules shape this:
   *   - Automation runs are exempt entirely — there is no one to nudge.
   *   - A dismissed band stays hidden for this thread. `blocked` has no dismiss, so it is never checked.
   *   - Notices are withheld for the whole active turn. The limit governs the NEXT turn, not the one
   *     in flight, so the composer keeps Stop reachable and the next applicable notice appears only
   *     after the genuine completion (not during an approval/interview pause). */
  protected readonly $contextGuardBand = computed<ContextGuardNoticeBand | null>(() => {

    if (this.$interactionMode() === 'automation') return null;

    const band = this.chatService.$contextGuardStatus()?.band;
    if (!band || band === 'fresh') return null;
    if (this.$contextGuardTurnActive() || this.chatService.$isStreaming()) return null;
    if (band === 'blocked') return 'blocked';

    const key = contextGuardDismissalKey(this.chatService.$threadId(), band);
    return this.$dismissedGuards().has(key) ? null : band;
  });

  /** Whether the composer is read-only under the terminal rail. Pending cards still win. */
  protected readonly $contextGuardBlocked = computed(() => this.$contextGuardBand() === 'blocked');

  /** The advisory/approaching notice to render above the composer, or null. */
  protected readonly $contextGuardNoticeBand = computed<ContextGuardDismissibleBand | null>(() => {

    const band = this.$contextGuardBand();
    return band === 'blocked' ? null : band;
  });

  /** Active helper hints — interview-card hints when the question card is up, none while a write-tool
   *  approval card is up (its Approve/Decline buttons are self-explanatory), a read-only explanation
   *  under the disabled conversation-limit composer, otherwise the prompt-input shortcuts. */
  protected readonly $activeHints = computed<ShortcutHint[]>(() => {

    // The unavailable notice outranks the cards in the composer slot, so their hints must not linger.
    if (this.$threadUnavailable()) return [];
    const qCard = this.$activeQuestionCard();
    if (qCard) {
      const currentQuestion = qCard.data.questions[0];
      const isLast = qCard.data.questions.length === 1;
      return AgentQuestionCardComponent.hintsForMode(currentQuestion.mode, isLast);
    }
    if (this.$activePendingApproval()) return [];
    if (this.$contextGuardBlocked()) return CONTEXT_GUARD_BLOCKED_HINTS;
    if (this.$usageLimitBlocked() || this.$providerUnavailable()) return [];
    return PROMPT_INPUT_HINTS;
  });

  // ─── Tenancy-wide AI spend notice ────────────────────────────────────────────

  private readonly $dismissedUsageLimits = signal<Set<string>>(loadDismissedUsageLimitNotices());

  // Refresh on open and at the end of every turn — the turn that just finished is exactly
  // what moved the tenancy's spend. The service dedupes concurrent refreshes.
  private readonly usageLimitRefresher = effect(() => {

    if (!this.chatService.$isStreaming()) void this.usageLimits.refresh();
  });

  /** The spend status to surface right now, or null when nothing should show.
   *  Blocked states always show; info/warning respect the reset-keyed dismissal. */
  protected readonly $usageLimitStatus = computed<AIUsageLimitStatus | null>(() => {

    const status = this.usageLimits.$status();
    if (!status) return null;
    if (status.assistantBlocked || status.blocked) return status;
    if (status.state === 'ok' || !status.message) return null;
    return this.$dismissedUsageLimits().has(usageLimitDismissalKey(status)) ? null : status;
  });

  /** The assistant's 90% cutoff (or the tenancy-wide 100%): the composer is replaced, matching the API's refusal. */
  protected readonly $usageLimitBlocked = computed(() => {

    const status = this.usageLimits.$status();
    return !!status && (status.assistantBlocked || status.blocked);
  });

  /** The above-composer nudge slot only ever shows the NON-blocking bands. A blocked status renders
   *  exclusively in the composer's place — never as a floating card above a live composer. */
  protected readonly $usageLimitNudgeStatus = computed(() => this.$usageLimitBlocked() ? null : this.$usageLimitStatus());

  protected onUsageLimitDismiss(): void {

    const status = this.usageLimits.$status();
    if (!status) return;
    this.$dismissedUsageLimits.set(markUsageLimitNoticeDismissed(usageLimitDismissalKey(status)));
  }

  // ─── Provider unavailability (the model provider's own limits, not our caps) ──

  protected readonly providerUnavailableTitle = AI_PROVIDER_UNAVAILABLE_TITLE;
  protected readonly providerUnavailableDetail = AI_PROVIDER_UNAVAILABLE_DETAIL;
  protected readonly $providerUnavailable = computed(() => this.chatService.$providerUnavailable());

  protected onProviderUnavailableRetry(): void {

    this.chatService.dismissProviderUnavailable();
  }

  // ─── Suggest actions for the welcome screen (role × context aware) ──────────

  protected readonly $suggestConfig = computed(() => {

    const role = mapRoleToSuggestRole(this.currentUser.$role()?.name);
    const context = this.contextService.$context();
    return SUGGEST_ACTIONS[role][context];
  });

  // ─── Forwarded service signals ──────────────────────────────────────────────

  protected get $messages() { return this.chatService.$messages; }
  protected get $isStreaming() { return this.chatService.$isStreaming; }
  protected get $lastMessageIsUser() { return this.chatService.$lastMessageIsUser; }
  protected get $revealedTextParts() { return this.revealService.$revealedTextParts; }
  protected get $cardActions() { return this.chatService.$cardActions; }
  protected get $collapsedActivityBlocks() { return this.chatService.$collapsedActivityBlocks; }
  protected get $blobUrls() { return this.chatService.$allSessionBlobUrls; }
  protected get $expandedTools() { return this.chatService.$expandedTools; }
  protected get $streamingError() { return this.chatService.$streamingError; }
  protected get $threadUnavailable() { return this.chatService.$threadUnavailable; }
  protected get $threadLoadError() { return this.chatService.$threadLoadError; }
  protected get $threadLoading() { return this.chatService.$threadLoading; }

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  public ngOnInit(): void {

    DesignSystem.Mount(this.document, agentThemeStyleId, agentThemeBridgeStyles);

    // Configure chatService with context signals (sourced from AgentContextService)
    this.chatService.configure({
      $mode: this.$mode,
      $currentJobId: this.contextService.$currentJobId,
      $currentWorkOrderId: this.contextService.$currentWorkOrderId,
      $interactionMode: this.$interactionMode,
    });

    // Initialize chat (without loading a thread — the effect below handles thread loading)
    this.chatService.initChat();

    this.setupDragAndDrop();

    // In popup mode the $threadId input is intentionally unbound — thread state
    // is owned by the popup-mode effect via panelService.$activeTabId, so this
    // effect must not call createNewThread when the input is null.
    effect(() => {
      const newThreadId = this.$threadId();
      const currentThreadId = untracked(() => this.chatService.$threadId());
      const isPopup = untracked(() => this.$mode()) === 'popup';
      // Untracked so the service's own signal reads and writes don't become triggers for this effect (see the popup effect).
      if (newThreadId && newThreadId !== currentThreadId) {
        untracked(() => this.chatService.switchThread(newThreadId));
      } else if (!newThreadId && currentThreadId && !isPopup) {
        untracked(() => this.chatService.createNewThread());
      }
      untracked(() => this.focusInputAfterPanelReady());
    }, { injector: this.injector });

    // Auto-scroll behavior:
    //  - Empty thread → clear follow + spacer.
    //  - Local send (pendingAnchorOnSend) → anchor the new user message to
    //    the top so they can read their question while the assistant
    //    streams below; auto-follow takes over once the reply overflows.
    //  - Anything else (initial render, history loading incrementally, a
    //    message from another tab) → enableAutoFollow so the viewport
    //    sticks to the bottom. Anchoring a non-locally-sent message would
    //    yank the user's view to a random point during incremental load.
    effect(() => {
      const messages = this.chatService.$messages();
      if (!messages || messages.length === 0) {
        untracked(() => this.messageListRef()?.clearFollow());
        this.lastSeenUserMessageId = null;
        this.hasRenderedInitialMessages = false;
        this.pendingAnchorOnSend = false;
        return;
      }

      let latestUserId: string | null = null;
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'user') { latestUserId = messages[i].id; break; }
      }

      untracked(() => {
        const list = this.messageListRef();
        if (!list) return;

        const newUserMessage = !!latestUserId && latestUserId !== this.lastSeenUserMessageId;
        const initialRender = !this.hasRenderedInitialMessages;
        if (!newUserMessage && !initialRender) return;

        this.hasRenderedInitialMessages = true;
        this.lastSeenUserMessageId = latestUserId;

        if (this.pendingAnchorOnSend && newUserMessage && latestUserId) {
          this.pendingAnchorOnSend = false;
          list.anchorUserMessageToTop(latestUserId);
          return;
        }
        list.enableAutoFollow();
      });
    }, { injector: this.injector });

    // On streaming completion (response finished or Stop pressed): refocus
    // the input and turn off auto-follow so any future content shifts (e.g.
    // markdown re-render) don't drag the viewport.
    effect(() => {
      const streaming = this.chatService.$isStreaming();
      if (streaming) {
        this.wasStreaming = true;
        // A new turn is underway — cancel any pending "gone quiet" trigger.
        untracked(() => this.clearFeedbackIdle());
        return;
      }
      // A card opening mid-stream flips the adapter to 'idle' (isStreaming false) even though the
      // turn isn't over — it's paused waiting for the user's pick/decision. Skip the end-of-turn
      // side-effects while EITHER an interview card OR a write-tool approval card is pending, or
      // clearFollow() collapses the anchor spacer and a short conversation strands the card at the
      // top. Stay latched; the real completion runs once the card is answered and the stream finishes.
      if (this.wasStreaming && !untracked(() => this.$activeQuestionCard()) && !untracked(() => this.$hasPendingApproval())) {
        this.wasStreaming = false;
        untracked(() => {
          this.$contextGuardTurnActive.set(false);
          this.focusInputAfterPanelReady();
          this.messageListRef()?.clearFollow();
          // A reply just completed — after a quiet spell (no further user message) the chat has
          // likely wound down, so arm the "gone quiet" trigger for the feedback prompt.
          this.scheduleFeedbackIdle();
        });
      }
    }, { injector: this.injector });

    // Reset the feedback prompt whenever the active thread changes — clear the idle trigger,
    // drop the active flag, and reload whether this thread was already rated/dismissed.
    effect(() => {
      const tid = this.chatService.$threadId();
      untracked(() => {
        this.clearFeedbackIdle();
        this.$contextGuardTurnActive.set(false);
        this.$feedbackActive.set(false);
        this.$feedbackHandled.set(tid ? isFeedbackHandled(tid) : false);
      });
    }, { injector: this.injector });

    // Activate the feedback prompt when a trigger first fires AND the global frequency cap allows.
    // Activation records one appearance and latches on for the thread (it stays shown across
    // streaming/hide until rated or dismissed), so a single conversation only ever counts once.
    effect(() => {
      if (!this.$feedbackTriggered()) return;
      untracked(() => {
        if (this.$feedbackActive() || this.$feedbackHandled()) return;
        const now = Date.now();
        const recent = loadRecentFeedbackShows(now);
        if (recent.length >= FEEDBACK_MAX_SHOWS) return;
        recordFeedbackShow(now, recent);
        this.$feedbackActive.set(true);
      });
    }, { injector: this.injector });

    // Emit threadChanged when chatService creates a new thread ID
    effect(() => {
      const tid = this.chatService.$threadId();
      if (tid) {
        untracked(() => this.threadChanged.emit(tid));
      }
    }, { injector: this.injector });

    // In popup mode, sync active tab changes from panel service
    effect(() => {
      const panelOpen = this.panelService.$panelOpen();
      if (!panelOpen || this.$mode() !== 'popup') return;

      const activeThreadId = this.panelService.$activeThreadId();
      const newChatOpen = this.panelService.$newChatOpen();
      const currentThreadId = untracked(() => this.chatService.$threadId());
      // A run detached to the background on minimize leaves an empty foreground adapter on the same threadId,
      // so reattach when the active thread is unchanged but the foreground has no messages (switchThread no-ops otherwise).
      const foregroundEmpty = untracked(() => this.chatService.$messages().length === 0);

      // Untracked: switchThread clears and re-reads the adapter's messages, so tracking its reads made each run
      // re-trigger this effect, and while a cold thread's history loaded (foreground still empty) it looped and froze the page.
      if (activeThreadId && (activeThreadId !== currentThreadId || foregroundEmpty)) {
        untracked(() => this.chatService.switchThread(activeThreadId));
      } else if (newChatOpen) {
        const alreadyFresh = untracked(() => {
          const messages = this.chatService.$messages();
          return messages.length === 0 && !this.chatService.$threadId();
        });
        if (!alreadyFresh) {
          untracked(() => this.chatService.createNewThread());
        }
      }
      untracked(() => this.focusInputAfterPanelReady());
    }, { injector: this.injector });

    // Detach stream to background when popup panel is minimized/closed
    effect(() => {
      const panelOpen = this.panelService.$panelOpen();
      if (panelOpen || this.$mode() !== 'popup') return;

      const isStreaming = untracked(() => this.chatService.$isStreaming());
      if (isStreaming) {
        // Untracked for the same reason as the thread switches above: detaching reads and swaps the
        // chat's own signals, which must not become triggers for this effect.
        untracked(() => this.chatService.detachToBackground());
      }
    }, { injector: this.injector });

    // Emit title updates from chatService for fullpage mode
    effect(() => {
      const update = this.chatService.$threadTitleUpdate();
      if (update) {
        this.titleUpdated.emit(update);
      }
    }, { injector: this.injector });
  }

  public ngOnDestroy(): void {

    // Unsubscribe any in-flight upload subscriptions
    for (const file of this.$pendingFiles()) {
      file.subscription?.unsubscribe();
    }

    this.teardownDragAndDrop();
    this.composerResizeObserver?.disconnect();
    this.clearFeedbackIdle();
    this.chatService.dispose();
  }

  // Focus the input as soon as the panel is actually visible. In fullpage mode
  // the panel-visibility signal stays true, so this focuses synchronously. In
  // popup mode we wait for the shell's measure-and-reveal cycle to flip the
  // signal, then focus once and stop watching.
  private focusInputAfterPanelReady(): void {

    // Focus once the composer view-child is actually in the DOM. In popup mode that also waits for the
    // shell's measure-and-reveal ($panelVisible); fullscreen has no such cycle. The threadId effect calls
    // this on route-nav into fullscreen BEFORE first render resolves promptInputRef(), so a synchronous
    // focus would silently no-op — fall back to a one-shot effect that focuses once both the (mode-
    // appropriate) readiness AND the composer view-child are present, then stops watching.
    // At most one watcher waits at a time: the composer can stay hidden (a card, a blocked notice, an
    // unavailable chat) across many tab switches, and each call used to leave another watcher alive.
    this.pendingFocusRef?.destroy();
    this.pendingFocusRef = null;
    const popupReady = this.$mode() !== 'popup' || this.panelService.$panelVisible();
    const input = this.promptInputRef();
    if (popupReady && input) {
      input.focus();
      return;
    }
    const ref = effect(() => {
      const ready = (this.$mode() !== 'popup' || this.panelService.$panelVisible()) && this.promptInputRef();
      if (ready) {
        untracked(() => this.promptInputRef()?.focus());
        ref.destroy();
        if (this.pendingFocusRef === ref) this.pendingFocusRef = null;
      }
    }, { injector: this.injector });
    this.pendingFocusRef = ref;
  }

  // ─── Feedback prompt ──────────────────────────────────────────────────────────

  /** Active (live) thread id from the chat service — exposed for the feedback prompt template. */
  protected get $currentThreadId() { return this.chatService.$threadId; }

  /** Arm the "gone quiet" trigger a while after a reply completes (reset if the user acts). */
  private scheduleFeedbackIdle(): void {

    this.clearFeedbackIdle();
    if (this.$feedbackHandled()) return;
    this.feedbackIdleTimer = setTimeout(() => this.$feedbackIdleReached.set(true), FEEDBACK_IDLE_MS);
  }

  private clearFeedbackIdle(): void {

    if (this.feedbackIdleTimer) { clearTimeout(this.feedbackIdleTimer); this.feedbackIdleTimer = null; }
    this.$feedbackIdleReached.set(false);
  }

  /** Called when the user rates or dismisses — suppress the prompt for this thread from now on. */
  protected onFeedbackHandled(): void {

    const threadId = this.chatService.$threadId();
    if (threadId) markFeedbackHandled(threadId);
    this.$feedbackHandled.set(true);
    this.clearFeedbackIdle();
  }

  // ─── Conversation-length guard ────────────────────────────────────────────────

  /** "Start a new chat" from the limit notice or the unavailable-conversation notice.
   *
   *  This deliberately does NOT call chatService.createNewThread() directly. Each surface already owns
   *  that lifecycle, and going around it would leave the rest of the UI pointing at the old chat:
   *   - popup: activate the reserved New Chat tab, exactly what the bottom bar's own button does. The
   *     container's popup effect then creates the thread, so the tab bar and chat stay in step.
   *   - fullscreen: ask the host page, which clears ?threadId and feeds a null [threadId] back in —
   *     the same path as the sidebar's New chat button, so the URL and thread list follow.
   *  Either way the existing draft carry-over in createNewThread() still applies. */
  protected onContextGuardNewChat(): void {

    if (this.$mode() === 'popup') {
      this.panelService.toggleTab(RESERVED_TAB_ID.NEW_CHAT);
      return;
    }
    this.newChatRequested.emit();
  }

  /** Dismiss the current advisory/approaching endcap for this thread and band. */
  protected onContextGuardDismiss(): void {

    const band = this.$contextGuardNoticeBand();
    if (!band) return;
    this.dismissContextGuardBand(band);
  }

  private dismissContextGuardBand(band: ContextGuardDismissibleBand): void {

    const key = contextGuardDismissalKey(this.chatService.$threadId(), band);
    this.$dismissedGuards.set(markContextGuardDismissed(key));
  }

  // ─── Message sending ────────────────────────────────────────────────────────

  protected onSend(event: { text: string; files: PendingFile[]; mentions: MentionItem[]; context: import('../../agent.types').SessionContext | null; modelTier: import('../../agent.types').AgentModelTier }): void {

    // The backend remains authoritative, but the visible disabled composer must not be bypassable
    // through a stale child event or synthetic keyboard submission. A still-loading thread is held
    // here too, before pending files are cleared, because the service would drop the turn anyway.
    if (this.$threadUnavailable() || this.$threadLoading() || this.$contextGuardBlocked() || this.$usageLimitBlocked()) return;

    const { text, files, modelTier } = event;
    if (!text.trim() && files.length === 0) return;

    // The gentle advisory has done its job once the user continues, so that send permanently retires
    // it for this thread. The approaching warning only hides for the active turn; unless its X was
    // clicked, the latest backend status makes it return after the agent genuinely finishes.
    if (this.chatService.$contextGuardStatus()?.band === 'advisory') {
      this.dismissContextGuardBand('advisory');
    }
    this.$contextGuardTurnActive.set(true);
    this.pendingAnchorOnSend = true;
    void this.chatService.sendMessage(text, files.length > 0 ? files : undefined, undefined, modelTier)
      .then(() => {
        // Normal completion clears the latch in the streaming effect above. This fallback covers a
        // request rejected before streaming ever starts, without releasing an approval-paused turn.
        if (!this.wasStreaming && !this.chatService.$isStreaming()) {
          this.$contextGuardTurnActive.set(false);
        }
      })
      .catch(() => this.$contextGuardTurnActive.set(false));

    // Clear pending files after sending
    this.$pendingFiles.set([]);
  }

  protected onStop(): void {

    this.chatService.stopStreaming();
  }

  // ─── Tool approval (HITL) ───────────────────────────────────────────────────

  protected async onApproveToolCall(toolCallId: string): Promise<void> {

    // Optimistically retire the overlay card so the input bar returns this frame, before the
    // resume stream flips the tool part out of 'approval-requested'. Only do so once we know the
    // approval was actually dispatched — a false result means it could not be resumed (the service
    // surfaces an error/retry alert), so the card must stay so the user isn't left at a dead end.
    const dispatched = await this.chatService.approveToolCall(toolCallId);
    if (dispatched) this.markApprovalResolved(toolCallId);
  }

  protected async onDeclineToolCall(event: { id: string; reason?: string }): Promise<void> {

    const dispatched = await this.chatService.declineToolCall(event.id, event.reason);
    if (dispatched) this.markApprovalResolved(event.id);
  }

  /** Record a write-tool approval/decline so $activePendingApproval drops it immediately. */
  private markApprovalResolved(callId: string): void {

    if (!callId) return;
    this.resolvedApprovalCallIds.update(set => set.has(callId) ? set : new Set(set).add(callId));
  }

  protected onQuestionSubmit(event: { toolCallId: string; action: CardAction; answers: AIInterviewAnswer[] }): void {

    const meta: InterviewCardActionMeta = {
      action: event.action,
      timestamp: Date.now(),
      answers: event.answers,
    };
    const message = this.findAssistantMessageForToolCall(event.toolCallId);
    if (message) {
      this.chatService.persistCardAction(message.id, event.toolCallId, meta);
    }

    // True HITL: the tool is paused on `needsApproval`. Forward the structured answers
    // through the approve path — the backend merges them into the request context so
    // the tool's resumed execute returns them as the actual tool result.
    void this.chatService.approveToolCall(event.toolCallId, {
      toolCallId: event.toolCallId,
      answers: event.answers,
      skipped: event.action === 'skipped',
    });
  }

  protected onCardActionComplete(event: { toolCallId: string; action: CardAction }): void {

    const meta: InterviewCardActionMeta = { action: event.action, timestamp: Date.now(), answers: [] };
    const message = this.findAssistantMessageForToolCall(event.toolCallId);
    if (message) {
      this.chatService.persistCardAction(message.id, event.toolCallId, meta);
    }

    // The interview tool is paused on `needsApproval`; dismissing it (X) is a full skip. Resume the
    // run through the same approve path the submit handler uses so the agent continues and leaves a
    // "Skipped" recap instead of staying suspended forever.
    void this.chatService.approveToolCall(event.toolCallId, {
      toolCallId: event.toolCallId,
      answers: [],
      skipped: true,
    });

    // While the card was open the streaming-completion effect latched but skipped clearFollow()
    // (because $activeQuestionCard() was truthy). On dismiss that signal goes null but the effect
    // does not re-run, so collapse the anchor spacer here or the conversation stays pinned off-screen
    // under a never-collapsed spacer and the chat looks blank.
    this.messageListRef()?.clearFollow();
  }

  protected onDraftChange(text: string): void {

    this.panelService.setComposerDraft(this.$draftKey(), text);
  }

  protected onActivityBlockToggled(blockKey: string): void {

    this.chatService.toggleActivityBlock(blockKey);
  }

  protected onToolExpandToggled(callId: string): void {

    this.chatService.toggleToolExpand(callId);
  }

  // ─── Welcome screen (suggest actions) ───────────────────────────────────────

  protected onSuggestPrompt(prompt: string): void {

    const promptInput = this.promptInputRef();
    if (promptInput) {
      promptInput.setText(prompt);
      promptInput.focus();
    }
  }

  // ─── File upload ────────────────────────────────────────────────────────────

  protected onFileSelected(files: File[]): void {

    this.addFiles(files);
  }

  protected onRemoveFile(id: string): void {

    const files = this.$pendingFiles();
    const target = files.find(f => f.id === id);
    if (target) {
      if (target.status === 'uploading') {
        this.uploadService.abort(id);
      }
      target.subscription?.unsubscribe();
      this.$pendingFiles.set(files.filter(f => f.id !== id));
    }
  }

  // ─── Drag & drop ───────────────────────────────────────────────────────────

  // In popup mode the container fills the whole panel, so it owns drag-and-drop on
  // its own host. In fullscreen the parent layout owns it instead, so the overlay
  // can also cover the agent conversations sidebar — there, dropped files arrive
  // via ingestFiles() and the container does not wire its own listeners.
  private setupDragAndDrop(): void {

    if (this.$mode() !== 'popup') {
      return;
    }
    const target = this.elementRef.nativeElement;
    this.dragListenerTarget = target;
    target.addEventListener('dragenter', this.boundDragEnter as EventListener);
    target.addEventListener('dragleave', this.boundDragLeave as EventListener);
    target.addEventListener('dragover', this.boundDragOver as EventListener);
    target.addEventListener('drop', this.boundDrop as EventListener);
  }

  // Entry point for drops handled by a parent layout (fullscreen mode), where the
  // drop region spans more than the chat container itself.
  public ingestFiles(files: File[]): void {

    this.addFiles(files);
  }

  private teardownDragAndDrop(): void {

    const target = this.dragListenerTarget;
    if (!target) {
      return;
    }
    target.removeEventListener('dragenter', this.boundDragEnter as EventListener);
    target.removeEventListener('dragleave', this.boundDragLeave as EventListener);
    target.removeEventListener('dragover', this.boundDragOver as EventListener);
    target.removeEventListener('drop', this.boundDrop as EventListener);
    this.dragListenerTarget = null;
  }

  // Only react to file drags; dragging text or page elements must not trip the overlay.
  private isFileDrag(event: DragEvent): boolean {

    if (this.$threadUnavailable() || this.$contextGuardBlocked() || this.$usageLimitBlocked()) return false;
    const types = event.dataTransfer?.types;
    return !!types && Array.from(types).includes('Files');
  }

  private onDragEnter(event: DragEvent): void {

    if (!this.isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    this.dragCounter++;
    this.$isDragOver.set(true);
  }

  private onDragLeave(event: DragEvent): void {

    if (!this.isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.$isDragOver.set(false);
    }
  }

  private onDrop(event: DragEvent): void {

    if (!this.isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    this.dragCounter = 0;
    this.$isDragOver.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.addFiles(Array.from(files));
    }
  }

  private onDragOver(event: DragEvent): void {

    if (!this.isFileDrag(event)) {
      return;
    }
    event.preventDefault();
  }

  // ─── Mention coordination ──────────────────────────────────────────────────

  protected onMentionSearch(query: string): void {

    this.$mentionQuery.set(query);
    this.$mentionVisible.set(query.length > 0);

    if (query.length === 0) {
      this.$mentionResults.set([]);
      return;
    }

    // TODO: search for matching jobs/customers/contacts via API or local cache
    // For now, results remain empty until the mention search API is wired up
    this.$mentionResults.set([]);
  }

  protected onMentionSelected(item: MentionItem): void {

    this.$mentionVisible.set(false);
    this.$mentionQuery.set('');
    this.$mentionResults.set([]);

    // Insert the mention into the prompt input text
    const promptInput = this.promptInputRef();
    if (promptInput) {
      const currentText = promptInput.$text();
      // Replace the @query at the end with the mention label
      const replaced = currentText.replace(/@\w*$/, `@${item.label} `);
      promptInput.setText(replaced);
      promptInput.addMention(item);
    }
  }

  protected onMentionDismissed(): void {

    this.$mentionVisible.set(false);
    this.$mentionQuery.set('');
    this.$mentionResults.set([]);
  }

  // ─── Error handling ─────────────────────────────────────────────────────────

  protected onRetry(): void {

    this.chatService.retryLastMessage();
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  /** Validate and queue files for upload */
  private addFiles(files: File[]): void {

    // Covers upload selection, pasted images, popup drop and fullscreen ingestFiles through one
    // central seam. The server still enforces the actual context boundary.
    if (this.$threadUnavailable() || this.$contextGuardBlocked() || this.$usageLimitBlocked()) return;

    for (const file of files) {
      if (this.$pendingFiles().length >= FILE_CONSTRAINTS.MAX_FILES_PER_MESSAGE) {
        console.warn(`[AgentChat] File limit reached (${FILE_CONSTRAINTS.MAX_FILES_PER_MESSAGE}) - skipping "${file.name}"`);
        break;
      }
      if (!FILE_CONSTRAINTS.ACCEPTED_TYPES.has(file.type) && !this.hasAcceptedExtension(file.name)) {
        console.warn(`[AgentChat] Rejected file "${file.name}" - unsupported type: ${file.type}`);
        continue;
      }
      if (file.size > FILE_CONSTRAINTS.MAX_FILE_SIZE) {
        console.warn(`[AgentChat] Rejected file "${file.name}" - exceeds 25 MB limit`);
        continue;
      }

      const id = v4();
      const pending: PendingFile = { file, id, progress: 0, status: 'uploading' };

      const subscription = this.uploadService.uploadFile(file, id).subscribe({
        next: (progress) => {
          this.$pendingFiles.update(list => list.map(f => f.id === id ? { ...f, progress: progress.progress, status: progress.status, blobUrl: progress.blobUrl ?? f.blobUrl, error: progress.error ?? f.error } : f));
        },
        error: (err) => {
          this.$pendingFiles.update(list => list.map(f => f.id === id ? { ...f, status: 'error', error: err?.message ?? 'Upload failed' } : f));
        },
      });

      pending.subscription = subscription;
      this.$pendingFiles.update(list => [...list, pending]);
    }
  }

  /**
   * Extension fallback for the file-type gate. Browsers frequently report email messages
   * (.eml/.msg) with a blank or application/octet-stream MIME type, so MIME alone would reject
   * them. Accept a file when its name ends with one of FILE_CONSTRAINTS.ACCEPTED_EXTENSIONS.
   */
  private hasAcceptedExtension(filename: string): boolean {

    const name = filename.toLowerCase();
    return FILE_CONSTRAINTS.ACCEPTED_EXTENSIONS
      .split(',')
      .some(extension => name.endsWith(extension.trim()));
  }

  /** Find the assistant message that owns a given tool call — used for card-action persistence. */
  private findAssistantMessageForToolCall(toolCallId: string): UIMessage | undefined {

    return this.chatService.$messages().find(m => m.role === 'assistant' && m.parts.some((p: unknown) => (p as { toolCallId?: string }).toolCallId === toolCallId));
  }
}
